/*global google,dataLayer*/

import Cookies from 'js-cookie';
import { keys } from '../constants';
import { isKeyEvent, getTarget } from '../helpers/eventHelper';
import AddressFormatter from './AddressFormatter';
import MegaModal from './MegaModal';
import GoogleAPILoader from '../GoogleMapsAPILoader';
import { showLoader, hideLoader } from './loader';

//switch on/off logging by using the nullLogger
import { logger, experimentLogger } from '../logger';
//import { nullLogger as logger, nullLogger as experimentLogger } from '../logger';

const filename = 'addresslookup.js';

export default function AddressLookup (selector, options = {}) {
    logger.time(`${filename} initialised`);
    logger.debug(filename, 'selector specified:', selector);
    logger.debug(filename, 'options provided:', options);

    //Google services used within this component
    this.geocoder = new google.maps.Geocoder();
    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
    this.sessionToken = new google.maps.places.AutocompleteSessionToken();

    /**
     * Define options - can be passed in or set as defaults
     *
     * context
     * onComplete
     */
    this.options = {
        /**
         * The context that the component is placed within
         * @type {node|document}
         */
        context: options.context || document,
        existingLocationData: options.existingLocationData,
        requiredFields: options.requiredFields || ['streetNumber', 'street', 'locality', 'postCode'],
        format: options.format || '{street}, {streetNumber}, {postcode}, {locality}',
        log: options.log,
        loggingConfig: options.loggingConfig ? Object.assign(options.loggingConfig, { event: 'trackEvent' }) : { event: 'trackEvent', eventCategory: 'addressLookup', eventAction: 'search' },
        geocoderSuggestions: options.geocoderSuggestions,
        types: options.types,
        addressRegex: options.addressRegex
    };
    logger.debug(filename, 'options being used:', this.options);

    this.addressFormatter = new AddressFormatter(this.options.format);

    this.callbacks = [];
    this.onComplete(options.onComplete);
    this.onFullAddressUpdated(options.onFullAddressUpdated);
    this.onStreetNumberUpdated(options.onStreetNumberUpdated);
    this.onSuggestionClicked(options.onSuggestionClicked);
    this.onSuggestionSelected(options.onSuggestionSelected);
    this.onConfirm(options.onConfirm);
    this.onSuggestionsDisplayed(options.onSuggestionsDisplayed);
    this.onNoSuggestions(options.onNoSuggestions);
    this.onFullAddressFocus(options.onFullAddressFocus);
    this.onFullAddressMissing(options.onFullAddressMissing);
    this.onRequestStreetNumber(options.onRequestStreetNumber);
    this.onRequestBeMoreSpecific(options.onRequestBeMoreSpecific);
    this.onGeocoderSuggestion(options.onGeocoderSuggestion);
    this.onGeoLocate(options.onGeoLocate);
    this.onGeoLocateError(options.onGeoLocateError);
    this.onGeoLocateSuggestions(options.onGeoLocateSuggestions);
    this.onGeoLocateSelect(options.onGeoLocateSelect);
    logger.debug(filename, 'option callbacks bound');

    function logAddressLookupEvent (loggingConfig, label, action, inputs) {
        try {
            const gid = Cookies.get('_gid');
            experimentLogger.info(`label:[${label}]`,
                `address:[${inputs.fullAddress.el.value}]`,
                `street number:[${inputs.streetNumber.value}]`,
                `gid:[${gid}]`);
            if (window.dataLayer) {
                dataLayer.push(Object.assign(loggingConfig, { eventAction: action, eventLabel: label }));
            }
        } catch (e) {
            logger.error('Error occurred while trying to log address lookup event to server', e);
        }
    }

    if (this.options.log && this.options.loggingConfig !== undefined) {
        this.onConfirm(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'search', 'click', this.lookup.inputs);
        });
        this.onComplete(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'valid', 'search', this.lookup.inputs);
        });
        this.onNoSuggestions(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'error - onNoSuggestions', 'search', this.lookup.inputs);
        });
        this.onFullAddressMissing(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'error - onFullAddressMissing', 'search', this.lookup.inputs);
        });
        this.onFullAddressFocus(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'address field', 'click', this.lookup.inputs);
        });
        this.onRequestStreetNumber(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'error - onRequestStreetNumber', 'search', this.lookup.inputs);
        });
        this.onRequestBeMoreSpecific(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'error - onRequestBeMoreSpecific', 'search', this.lookup.inputs);
        });
        this.onGeocoderSuggestion(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'error - onGeocoderSuggestion', 'search', this.lookup.inputs);
        });
        this.onGeoLocate(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'locate_me - start', 'search', this.lookup.inputs);
        });
        this.onGeoLocateError(() => {
            logAddressLookupEvent(this.options.loggingConfig, `locate_me - exception - ${this.lookup.geolocateBtn.errorMessage}`, 'search', this.lookup.inputs);
        });
        this.onGeoLocateSuggestions(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'locate_me - suggested', 'search', this.lookup.inputs);
        });
        this.onGeoLocateSelect(() => {
            logAddressLookupEvent(this.options.loggingConfig, 'locate_me - success', 'search', this.lookup.inputs);
        });

        logger.debug(filename, 'dataLayer push callbacks bound');
    }

    //Define the address lookup model
    this.lookup = {
        el: this.options.context.querySelector(selector)
    };
    this.lookup.inputs = {
        fullAddress: {
            el: this.lookup.el.querySelector('[data-address-lookup-full-address]'),
            dirty: false
        },
        streetNumber: {
            el: this.lookup.el.querySelector('[data-address-lookup-street-number]')
        }
    };
    this.lookup.suggestions = {
        el: this.lookup.el.querySelector('[data-address-lookup-suggestions]'),
        values: [],
        selected: 0,
        visible: false
    };
    if (navigator.geolocation) {
        this.lookup.geolocateBtn = {
            el: this.lookup.el.querySelector('[data-address-lookup-geolocate-button]'),
            errorMessage: '',
            locationsFound: false,
            isDisabled: false,
            loader: {
                el: this.lookup.el.querySelector('[data-address-lookup-geolocate-loader]')
            }
        };
    } else {
        this.lookup.el.querySelector('[data-address-lookup-geolocate-button]').remove();
    }
    this.lookup.submitBtn = {
        el: this.lookup.el.querySelector('[data-address-lookup-search-button]')
    };
    logger.debug(filename, 'component elements defined');

    //Assign options from data attributes
    this.options.componentRestrictions = options.componentRestrictions;

    this.address = {
        regex: new RegExp(this.options.addressRegex, 'g')
    };

    if (this.options.existingLocationData) {
        const defaultFullAddress = this.formatSuggestionForFullAddress(this.options.existingLocationData),
            input = this.lookup.inputs.fullAddress.el;
        logger.time(`${filename} existing address animated`);
        input.setAttribute('data-test-animstate', 'running');
        for (let i = 0; i < defaultFullAddress.length; i++) {
            (function (x) {
                setTimeout(() => {
                    input.value = defaultFullAddress.substring(0, x + 1);
                }, 30 * x);
            }(i));
        }
        setTimeout(() => {
            input.setAttribute('data-test-animstate', 'finished');
        }, 30 * defaultFullAddress.length);
        logger.timeEnd(`${filename} existing address animated`);
    }

    //Bind Events
    this.lookup.inputs.fullAddress.el.addEventListener('keyup', (e) => this.fullAddressInput(e));
    this.lookup.inputs.fullAddress.el.addEventListener('blur', () => this.fullAddressBlur());
    this.lookup.inputs.fullAddress.el.addEventListener('focus', () => this.fullAddressFocus());
    this.lookup.inputs.streetNumber.el.addEventListener('keydown', (e) => this.streetNumberInput(e));
    this.lookup.suggestions.el.addEventListener('click', (e) => this.suggestionClicked(e));
    this.lookup.submitBtn.el.addEventListener('click', () => this.confirm());
    if (this.lookup.geolocateBtn && this.lookup.geolocateBtn.el) {
        this.lookup.geolocateBtn.el.addEventListener('click', () => this.geolocate());
        this.lookup.geolocateBtn.el.addEventListener('blur', () => this.fullAddressBlur());
    }
    logger.debug(filename, 'events bound');

    logger.timeEnd(`${filename} initialised`);
}

AddressLookup.prototype.fullAddressBlur = function () {
    logger.debug(filename, 'fullAddressBlur');
    clearTimeout(this.fullAddressBlur.setTimeoutId);
    this.fullAddressBlur.setTimeoutId = setTimeout(() => {
        this.lookup.el.classList.remove('has-suggestionsDisplayed');
        this.lookup.suggestions.visible = false;
        if (this.lookup.geolocateBtn) {
            this.lookup.geolocateBtn.isDisabled = false;
        }
    }, 200);
};

AddressLookup.prototype.fullAddressFocus = function () {
    logger.debug(filename, 'fullAddressFocus');
    this.lookup.geolocateBtn.locationsFound = false;
    this.runCBs('onFullAddressFocus');
    if (this.lookup.suggestions.values.length) {
        this.displaySuggestions();
    }
};

AddressLookup.prototype.clearRequestingStreetNumber = function (el) {
    el.removeAttribute('data-test-is-requestingStreetNumber');
    el.classList.remove('is-requestingStreetNumber');
};

AddressLookup.prototype.setRequestingStreetNumber = function (el) {
    el.setAttribute('data-test-is-requestingStreetNumber', '');
    el.classList.add('is-requestingStreetNumber');
};

AddressLookup.prototype.fullAddressInput = function (e) {
    logger.debug(filename, 'fullAddressInput');
    this.disabled = false;
    const enterKeypressed = isKeyEvent(e, keys.ENTER),
        leftKeyPressed = isKeyEvent(e, keys.LEFTARROW),
        upKeyPressed = isKeyEvent(e, keys.UPARROW),
        rightKeyPressed = isKeyEvent(e, keys.RIGHTARROW),
        downKeyPressed = isKeyEvent(e, keys.DOWNARROW),
        tabKeyPressed = isKeyEvent(e, keys.TAB);

    if (this.isPostcodeSearch()) {
        this.clearSuggestionsList();
        this.showPostcodeSearchWarning();
        return;
    }

    if ((leftKeyPressed || rightKeyPressed || upKeyPressed || downKeyPressed) && !this.lookup.suggestions.visible && this.lookup.suggestions.values.length) {
        //Show suggestion list if one exists and it's not visible
        this.displaySuggestions();
        return;
    }
    if ((upKeyPressed || downKeyPressed) && this.lookup.suggestions.values.length) {
        //Navigate up and down displayed suggestion list
        if (upKeyPressed && this.lookup.suggestions.selected > 0) {
            this.lookup.suggestions.selected -= 1;
        }
        if (downKeyPressed && this.lookup.suggestions.selected < (this.lookup.suggestions.values.length - 1)) {
            this.lookup.suggestions.selected += 1;
        }
        this.updateSuggestionSelected();
        return;
    }
    if (leftKeyPressed || rightKeyPressed) {
        //IGNORE
        return;
    }
    if (enterKeypressed && this.lookup.suggestions.values.length) {
        //Select the currently displayed and highlighted/selected suggestion
        if (this.lookup.suggestions.values[this.lookup.suggestions.selected]) {
            this.lookup.inputs.fullAddress.el.value = this.lookup.suggestions.values[this.lookup.suggestions.selected].formattedAddress;
            this.getSuggestionDetails(this.lookup.suggestions.values[this.lookup.suggestions.selected], this.proceedAfterSuggestionSelected);
            this.runCBs('onSuggestionSelected');
        }
        return;
    }
    if (tabKeyPressed) {
        //IGNORE
        return;
    }
    this.resetUI();
    clearTimeout(this.fullAddressInput.setTimeoutId);
    this.fullAddressInput.setTimeoutId = setTimeout(() => {
        if (!this.lookup.inputs.fullAddress.el.value || !this.lookup.inputs.fullAddress.el.value.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '').length) {
            this.showFullAddressMissing();
            return;
        }
        this.lookup.inputs.fullAddress.dirty = true;
        this.runCBs('onFullAddressUpdated');
        this.processAddressGooglePlaces(this.lookup.inputs.fullAddress.el.value, (results) => this.showSuggestionsFromAddressPlacesResult(results));
    }, 500);
};

AddressLookup.prototype.suggestionClicked = function (e) {
    logger.debug(filename, 'suggestionClicked');
    this.lookup.suggestions.selected = getTarget(e).closest('[data-suggestion]').getAttribute('data-suggestion');
    if (this.lookup.suggestions.values[this.lookup.suggestions.selected] && this.lookup.suggestions.values[this.lookup.suggestions.selected].formattedAddress) {
        this.lookup.inputs.fullAddress.el.value = this.lookup.suggestions.values[this.lookup.suggestions.selected].formattedAddress;
        this.getSuggestionDetails(this.lookup.suggestions.values[this.lookup.suggestions.selected], this.proceedAfterSuggestionSelected);
        if (this.lookup.geolocateBtn.locationsFound) {
            this.runCBs('onGeoLocateSelect');
        }
        this.runCBs('onSuggestionClicked');
    } else {
        this.resetUI();
        this.processAddressGooglePlaces(this.lookup.inputs.fullAddress.el.value, (results) => this.showSuggestionsFromAddressPlacesResult(results));
    }
};

AddressLookup.prototype.streetNumberInput = function (e) {
    logger.debug(filename, 'streetNumberInput');
    const enterKeypressed = isKeyEvent(e, keys.ENTER),
        leftKeyPressed = isKeyEvent(e, keys.LEFTARROW),
        upKeyPressed = isKeyEvent(e, keys.UPARROW),
        rightKeyPressed = isKeyEvent(e, keys.RIGHTARROW),
        downKeyPressed = isKeyEvent(e, keys.DOWNARROW),
        tabKeyPressed = isKeyEvent(e, keys.TAB);

    if (leftKeyPressed || rightKeyPressed || upKeyPressed || downKeyPressed || tabKeyPressed) {
        return;
    }
    if (enterKeypressed) {
        this.confirm();
        return;
    }
    const self = this;
    clearTimeout(this.streetNumberInput.setTimeoutId);
    this.streetNumberInput.setTimeoutId = setTimeout(() => {
        if (!self.lookup.suggestions.values[self.lookup.suggestions.selected]) {
            self.resetUI();
            return;
        }
        if (!self.lookup.inputs.streetNumber.el.value.trim().length) {
            self.lookup.suggestions.values[self.lookup.suggestions.selected].streetNumber = null;
        }
        this.runCBs('onStreetNumberUpdated');
    }, 100);
};

AddressLookup.prototype.processAddressGooglePlaces = function (address, resultCallback) {
    logger.debug(filename, 'processAddressGooglePlaces', address);
    this.autocompleteService.getPlacePredictions({
        input: address,
        componentRestrictions: this.options.componentRestrictions,
        types: this.options.types,
        sessionToken: this.sessionToken
    }, resultCallback);
};

AddressLookup.prototype.updateSelectedSuggestion = function (results) {
    logger.debug(filename, 'updateSelectedSuggestion', results);
    const suggestions = AddressLookup.filterResultsIntoUniqueSuggestions(results, this.options.types);
    if (suggestions.length) {
        this.lookup.suggestions.values = { 0: suggestions[0] };
    } else {
        this.lookup.suggestions.values = { 0: this.lookup.suggestions.values[0] };
    }
    this.lookup.suggestions.selected = 0;
    this.lookup.inputs.fullAddress.el.value = this.lookup.suggestions.values[this.lookup.suggestions.selected].formattedAddress;
    this.getSuggestionDetails(this.lookup.suggestions.values[this.lookup.suggestions.selected]);
};

AddressLookup.prototype.showSuggestionsFromAddressPlacesResult = function (results) {

    logger.debug(filename, 'showSuggestionsFromAddressPlacesResult', results);
    if (this.isInvalidPostcodeFormat(this.lookup.inputs.fullAddress.el.value)
        && this.options.addressRegex
    ) {
        this.showPostcodeSearchWarning();
        return;
    }

    this.lookup.suggestions.values = AddressLookup.filterResultsIntoUniqueSuggestions(results, this.options.types);
    if (this.options.geocoderSuggestions && !this.lookup.suggestions.values.length) {
        this.runCBs('onGeocoderSuggestion');
        this.processAddressGoogleGeocoder({ address: this.lookup.inputs.fullAddress.el.value }, (geocoderResults) => this.showSuggestionsFromAddressGeocoderResult(geocoderResults));
    } else {
        this.updateSuggestionsList();
    }
};

AddressLookup.prototype.showSuggestionsFromAddressGeocoderResult = function (results) {
    logger.debug(filename, 'showSuggestionsFromAddressGeocoderResult', results);
    this.lookup.suggestions.values = AddressLookup.filterResultsIntoUniqueSuggestions(results, ['street_number']);
    this.updateSuggestionsList();
};

AddressLookup.prototype.resetUI = function () {
    logger.debug(filename, 'resetUI');
    this.lookup.suggestions.visible = false;
    this.lookup.el.classList.remove('has-suggestionsDisplayed', 'has-noSuggestions', 'has-fullAddressMissing', 'is-requestingBeMoreSpecific', 'is-unableToFindLocation', 'has-postcodeSearchWarning');
    this.clearRequestingStreetNumber(this.lookup.el);
    this.lookup.submitBtn.el.classList.remove('is-loading', 'is-loading--light', 'is-loading--small');
    this.lookup.inputs.streetNumber.el.value = '';
    this.clearSuggestionsList();
};

AddressLookup.prototype.clearSuggestionsList = function () {
    logger.debug(filename, 'clearSuggestionsList');
    // Clear the address input timeouts
    clearTimeout(this.fullAddressInput.setTimeoutId);
    clearTimeout(this.fullAddressBlur.setTimeoutId);
    clearTimeout(this.fullAddressFocus.setTimeoutId);
    this.lookup.el.classList.remove('has-suggestionsDisplayed');
    this.lookup.suggestions.visible = false;
    this.lookup.suggestions.values = [];
    this.lookup.suggestions.selected = 0;
    this.lookup.inputs.streetNumber.el.value = '';
    while (this.lookup.suggestions.el.firstChild) {
        this.lookup.suggestions.el.removeChild(this.lookup.suggestions.el.firstChild);
    }
    if (this.lookup.geolocateBtn.el) {
        this.lookup.geolocateBtn.el.classList.remove('is-switchedOn');
    }
};

AddressLookup.prototype.showPostcodeSearchWarning = function () {
    logger.debug(filename, 'showPostcodeSearchWarning');
    this.runCBs('onPostcodeSearch');
    if (!this.lookup.el.classList.contains('has-postcodeSearchWarning')) {
        this.lookup.el.classList.add('has-postcodeSearchWarning');
    }
};

AddressLookup.prototype.updateSuggestionsList = function () {
    logger.debug(filename, 'updateSuggestionsList');
    if (this.lookup.suggestions.values.length === 0) {
        this.lookup.el.classList.add('has-noSuggestions');
        this.runCBs('onNoSuggestions');
        return;
    }

    let first = true;
    for (const key in this.lookup.suggestions.values) {
        if (this.lookup.suggestions.values.hasOwnProperty(key)) {
            const suggestion = document.createElement('li');
            suggestion.setAttribute('data-suggestion', key);
            suggestion.classList.add('addressLookup-suggestions-item');
            suggestion.innerHTML = AddressLookup.formatSuggestionForListItem(this.lookup.suggestions.values[key]);
            if (first) {
                suggestion.classList.add('is-selected');
                first = false;
            }
            this.lookup.suggestions.el.appendChild(suggestion);
        }
    }
    this.displaySuggestions();
};

AddressLookup.prototype.processAddressGoogleGeocoder = function (options, resultCallback) {
    logger.debug(filename, 'processAddressGoogleGeocoder', options);
    this.geocoder.geocode({
        address: options.address,
        location: options.location
    }, resultCallback);
};

AddressLookup.prototype.updateSuggestionSelected = function () {
    logger.debug(filename, 'updateSuggestionSelected');
    Array.prototype.forEach.call(this.lookup.suggestions.el.querySelectorAll('[data-suggestion]'), (el) => {
        el.classList.remove('is-selected');
    });
    const selected = this.lookup.suggestions.el.querySelector(`[data-suggestion="${this.lookup.suggestions.selected}"]`);
    selected.classList.add('is-selected');
    //Scroll The selected suggestion into view.
    if (this.lookup.suggestions.el.offsetHeight < selected.offsetTop + selected.scrollHeight) {
        this.lookup.suggestions.el.scrollTop = this.lookup.suggestions.el.scrollTop + selected.offsetTop + selected.scrollHeight - this.lookup.suggestions.el.offsetHeight;
    } else if (selected.offsetTop < this.lookup.suggestions.el.scrollTop) {
        this.lookup.suggestions.el.scrollTop = selected.offsetTop;
    }
};

AddressLookup.prototype.getSuggestionDetails = function (suggestion, onComplete) {
    logger.debug(filename, 'getSuggestionDetails');
    const self = this;

    const fields = [
        'address_components',
        'formatted_address',
        'place_id',
        'geometry',
        'types',
        'website',
        'name'
    ];

    this.placesService.getDetails({
        placeId: suggestion.place_id,
        sessionToken: this.sessionToken,
        fields
    }, (place) => {
        suggestion.unitNumber = AddressLookup.findUnitNumber(place.address_components);
        suggestion.streetNumber = AddressLookup.findStreetNumber(place.address_components);
        suggestion.street = AddressLookup.findStreet(place.address_components);
        suggestion.locality = AddressLookup.findLocality(place.address_components);
        suggestion.sublocality = AddressLookup.findSublocality(place.address_components);
        suggestion.state = AddressLookup.findState(place.address_components);
        suggestion.postCode = AddressLookup.findPostalCode(place.address_components);
        suggestion.formattedAddress = place.formatted_address;
        suggestion.name = place.name;
        if (place.website) {
            suggestion.website = place.website;
        }
        suggestion.types = place.types;
        suggestion.latitude = place.geometry.location.lat();
        suggestion.longitude = place.geometry.location.lng();
        if (!suggestion.streetNumber && this.lookup.inputs.streetNumber.el.value.trim().length) {
            suggestion.streetNumber = self.lookup.inputs.streetNumber.el.value;
        }
        if (suggestion.streetNumber && suggestion.street && suggestion.locality && !suggestion.postCode) {
            self.geocoder.geocode({
                location: {
                    lat: suggestion.latitude,
                    lng: suggestion.longitude
                }
            },
            (results) => {
                for (let i = 0; i < results.length; i++) {
                    suggestion.postCode = AddressLookup.findPostalCode(results[i].address_components);
                    if (suggestion.postCode) {
                        i = results.length;
                    }
                }
                if (typeof onComplete === 'function') {
                    onComplete.call(self, suggestion);
                }
            });
        } else if (typeof onComplete === 'function') {
            onComplete.call(self, suggestion);
        }
    });

    this.sessionToken = new google.maps.places.AutocompleteSessionToken();
};

AddressLookup.prototype.proceedAfterSuggestionSelected = function (suggestion) {
    logger.debug(filename, 'proceedAfterSuggestionSelected');
    if (this.lookup.inputs.streetNumber.el.value.trim().length && !suggestion.streetNumber) {
        suggestion.streetNumber = this.lookup.inputs.streetNumber.el.value;
    }
    const missingFields = AddressLookup.suggestionMissingFields(suggestion, this.options.requiredFields);
    if (missingFields.length === 0) {
        delete suggestion.matched_substrings;
        suggestion.fullAddress = this.formatSuggestionForFullAddress(suggestion);
        this.clearRequestingStreetNumber(this.lookup.el);
        this.lookup.el.classList.remove('is-requestingBeMoreSpecific');
        this.runCBs('onComplete', suggestion);
    }
    this.lookup.el.classList.remove('has-suggestionsDisplayed');
    this.lookup.suggestions.visible = false;
    if (suggestion.street && this.options.requiredFields.includes('street') && missingFields.includes('streetNumber')) {
        this.showRequestStreetNumber();
        this.lookup.inputs.streetNumber.el.focus();
    } else if (missingFields.length) {
        this.clearRequestingStreetNumber(this.lookup.el);
        this.lookup.el.classList.add('is-requestingBeMoreSpecific');
        this.runCBs('onRequestBeMoreSpecific');
    }
};

AddressLookup.prototype.isPostcodeSearch = function () {
    return (this.options.addressRegex && this.address.regex.test(this.lookup.inputs.fullAddress.el.value));
};

AddressLookup.suggestionMissingFields = function (suggestion, requiredFields) {
    logger.debug(filename, 'suggestionMissingFields');
    let i;
    const missingFields = [];
    for (i = 0; i < requiredFields.length; i++) {
        if (!suggestion[requiredFields[i]]) {
            missingFields.push(requiredFields[i]);
        }
    }
    return missingFields;
};


AddressLookup.prototype.displayMultilineSuggestions = function () {
    const suggestions = this.lookup.suggestions.el.children;
    for (let i = 0; i < suggestions.length; i++) {
        suggestions[i].classList.add('addressLookup-suggestions--multiline');
        const htmlString = suggestions[i].innerHTML;
        const pos = htmlString.indexOf(',');
        if (htmlString.indexOf('<p') < 0 && pos > 0) {
            suggestions[i].innerHTML = `${htmlString.substr(0, pos)}<p class='addressLookup-suggestions--multiline-detail'>${htmlString.substr(pos + 1)}</p>`;
        }
    }
};

AddressLookup.prototype.displaySuggestions = function () {
    logger.debug(filename, 'displaySuggestions');
    this.lookup.el.classList.add('has-suggestionsDisplayed');
    this.lookup.suggestions.visible = true;
    this.lookup.suggestions.selected = 0;
    if (this.lookup.geolocateBtn) {
        this.lookup.geolocateBtn.isDisabled = false;
    }
    this.runCBs('onSuggestionsDisplayed');

    const multilineElement = document.body.querySelector('[data-address-lookup-suggestions]');
    if (multilineElement) {
        const isMultilineEnabled = multilineElement.getAttribute('data-multiline-suggestion-enabled');
        if (isMultilineEnabled === 'true') {
            this.displayMultilineSuggestions();
        }
    }
};

AddressLookup.prototype.confirm = function () {
    logger.debug(filename, 'confirm');
    this.runCBs('onConfirm');
    if (this.disabled) {
        return;
    }
    if (!this.lookup.inputs.fullAddress.el.value || !this.lookup.inputs.fullAddress.el.value.trim().length) {
        this.showFullAddressMissing();
    } else if (this.lookup.suggestions.values[this.lookup.suggestions.selected]) {
        this.lookup.inputs.fullAddress.el.value = this.lookup.suggestions.values[this.lookup.suggestions.selected].formattedAddress;
        this.getSuggestionDetails(this.lookup.suggestions.values[this.lookup.suggestions.selected], this.proceedAfterSuggestionSelected);
    } else {
        this.processAddressGooglePlaces(this.lookup.inputs.fullAddress.el.value, (results) => this.showSuggestionsFromAddressPlacesResult(results));
    }
};

AddressLookup.prototype.showFullAddressMissing = function () {
    logger.debug(filename, 'showFullAddressMissing');
    this.runCBs('onFullAddressMissing');
    if (this.lookup.el.classList.contains('has-fullAddressMissing')) {
        this.lookup.el.classList.add('has-fullAddressMissingAgain');
        setTimeout(() => { this.lookup.el.classList.remove('has-fullAddressMissingAgain'); }, 600);
    } else {
        this.lookup.el.classList.add('has-fullAddressMissing');
    }
};

AddressLookup.prototype.showRequestStreetNumber = function () {
    logger.debug(filename, 'showRequestStreetNumber');
    this.runCBs('onRequestStreetNumber');
    if (this.lookup.el.classList.contains('is-requestingStreetNumber')) {
        this.lookup.el.classList.add('is-requestingStreetNumberAgain');
        setTimeout(() => { this.lookup.el.classList.remove('is-requestingStreetNumberAgain'); }, 600);
    } else {
        this.setRequestingStreetNumber(this.lookup.el);
    }
};

AddressLookup.prototype.formatSuggestionForFullAddress = function (suggestion) {
    return this.addressFormatter.format(suggestion);
};

AddressLookup.prototype.geolocate = function () {
    logger.info(filename, 'geolocate requested');
    this.runCBs('onGeoLocate');
    this.resetUI();
    this.lookup.inputs.fullAddress.dirty = true;

    const options = {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
    };

    const geolocateSuccess = position => {
        if (!this.lookup.geolocateBtn.isDisabled) {
            this.lookup.geolocateBtn.isDisabled = true;
            const geolocation = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);
            this.processAddressGoogleGeocoder({
                location: geolocation
            },
            (geocoderResults) => this.showSuggestionsFromAddressGeocoderResult(geocoderResults));
            hideLoader(this.lookup.geolocateBtn.loader.el);
            this.lookup.geolocateBtn.el.classList.add('is-switchedOn');
            this.displaySuggestions();
            this.runCBs('onGeoLocateSuggestions');
        }
    };

    const geolocateError = error => {
        if (error.code === 0) {
            this.lookup.geolocateBtn.errorMessage = 'geolocate unkown error';
        } else if (error.code === 1) {
            this.lookup.geolocateBtn.errorMessage = 'geolocate permission denied';
        } else if (error.code === 2) {
            this.lookup.geolocateBtn.errorMessage = 'geolocate position unavailable';
        } else if (error.code === 3) {
            this.lookup.geolocateBtn.errorMessage = 'geolocate timeout';
        } else {
            this.lookup.geolocateBtn.errorMessage = `geolocate error code: ${error.code}`;
        }
        logger.error(filename, this.lookup.geolocateBtn.errorMessage);
        this.runCBs('onGeoLocateError');
        hideLoader(this.lookup.geolocateBtn.loader.el);
        this.lookup.el.classList.add('is-unableToFindLocation');
        this.lookup.geolocateBtn.isDisabled = false;
    };

    showLoader(this.lookup.geolocateBtn.loader.el, { isSmall: true, isDark: true, isOpaque: true });
    navigator.geolocation.getCurrentPosition(geolocateSuccess, geolocateError, options);
};

/**
 * Checks that the postcode entered passes the given regex test.
 *
 * @Regex: Tests that the input field starts with an integer.
 * @returns {boolean}
 */
AddressLookup.prototype.isInvalidPostcodeFormat = function (postcodeFormat) {
    if (this.address
        && this.address.regex
        && this.options
        && this.options.addressRegex
    ) {
        return new RegExp(this.options.addressRegex, 'g').test(postcodeFormat);
    }

    return false;
};

AddressLookup.filterResultsIntoUniqueSuggestions = function (results, types) {
    logger.debug(filename, 'filterResultsIntoUniqueSuggestions');
    const suggestions = [];
    let suggestion;
    const uniqueKeys = {};
    for (const key in results) {
        if (results.hasOwnProperty(key)) {
            if ((results[key].formatted_address && results[key].types.includes('street_address'))
                || (results[key].types.includes('street_address') || results[key].types.includes('route'))
                || (!types && results[key].types.includes('locality'))
                || results[key].types.includes('establishment')
                || results[key].types.includes('premise')) {
                suggestion = this.resultToSuggestion(results[key]);
                if (suggestion && !uniqueKeys[suggestion.place_id]) {
                    uniqueKeys[suggestion.place_id] = suggestion;
                    suggestions.push(suggestion);
                }
            }
        }
    }
    return suggestions;
};

AddressLookup.formatSuggestionForListItem = function (suggestion, options = { openTag: '<strong>', closeTag: '</strong>' }) {
    logger.debug(filename, 'formatSuggestionForListItem');
    let suggestionFormattedAddress = suggestion.formattedAddress;
    if (suggestion.matched_substrings) {
        for (let i = suggestion.matched_substrings.length - 1; i >= 0; i--) {
            const closeTagPos = suggestion.matched_substrings[i].offset + suggestion.matched_substrings[i].length;
            suggestionFormattedAddress = [suggestionFormattedAddress.slice(0, closeTagPos), options.closeTag, suggestionFormattedAddress.slice(closeTagPos)].join('');

            const startTagPos = suggestion.matched_substrings[i].offset;
            suggestionFormattedAddress = [suggestionFormattedAddress.slice(0, startTagPos), options.openTag, suggestionFormattedAddress.slice(startTagPos)].join('');
        }
    }
    return suggestionFormattedAddress;
};

AddressLookup.formatSuggestionComponent = function (suggestionComponent, options = {}) {
    logger.debug(filename, 'formatSuggestionComponent');
    return suggestionComponent + (options.delimiter || '');
};

AddressLookup.resultToSuggestion = function (result) {
    logger.debug(filename, 'resultToSuggestion');
    const suggestion = {};
    suggestion.description = result.description;
    suggestion.formattedAddress = result.description || result.formatted_address;
    suggestion.matched_substrings = result.matched_substrings;
    suggestion.place_id = result.place_id;
    return suggestion;
};

AddressLookup.findSpecificAddressComponentObject = function (componentNames, addressComponents) {
    logger.debug(filename, 'findSpecificAddressComponentObject');
    for (let i = 0; i < addressComponents.length; i++) {
        for (let j = 0; j < addressComponents[i].types.length; j++) {
            for (let k = 0; k < componentNames.length; k++) {
                if (addressComponents[i].types[j] === componentNames[k]) {
                    return addressComponents[i];
                }
            }
        }
    }
    return false;
};

AddressLookup.findUnitNumber = function (addressComponents) {
    logger.debug(filename, 'findUnitNumber', addressComponents);
    const addressComponent = AddressLookup.findSpecificAddressComponentObject(['subpremise'], addressComponents);
    return addressComponent && (addressComponent.short_name || addressComponent.long_name);
};

AddressLookup.findSpecificAddressComponent = function (componentNames, addressComponents) {
    logger.debug(filename, 'findSpecificAddressComponent', componentNames, addressComponents);
    const addressComponent = AddressLookup.findSpecificAddressComponentObject(componentNames, addressComponents);
    return addressComponent && (addressComponent.long_name || addressComponent.short_name);
};

AddressLookup.findPostalCode = function (addressComponents) {
    logger.debug(filename, 'findPostalCode', addressComponents);
    return AddressLookup.findSpecificAddressComponent(['postal_code', 'postal code'], addressComponents);
};

AddressLookup.findStreet = function (addressComponents) {
    logger.debug(filename, 'findStreet', addressComponents);
    return AddressLookup.findSpecificAddressComponent(['route'], addressComponents);
};

AddressLookup.findStreetNumber = function (addressComponents) {
    logger.debug(filename, 'findStreetNumber', addressComponents);
    return AddressLookup.findSpecificAddressComponent(['street_number'], addressComponents);
};

AddressLookup.findLocality = function (addressComponents) {
    logger.debug(filename, 'findLocality', addressComponents);
    return AddressLookup.findSpecificAddressComponent(['locality', 'administrative_area_level_3', 'administrative_area_level_2', 'administrative_area_level_1', 'postal_town'], addressComponents);
};

AddressLookup.findSublocality = function (addressComponents) {
    logger.debug(filename, 'findLocality', addressComponents);
    return AddressLookup.findSpecificAddressComponent(['sublocality', 'sublocality_level_1'], addressComponents);
};

AddressLookup.findState = function (addressComponents) {
    logger.debug(filename, 'findState', addressComponents);
    const addressComponent = AddressLookup.findSpecificAddressComponentObject(['administrative_area_level_1'], addressComponents);
    return addressComponent && (addressComponent.short_name || addressComponent.long_name);
};

AddressLookup.prototype.on = function (eventName, cb) {
    if (!this.callbacks[eventName]) {
        this.callbacks[eventName] = [];
    }
    if (typeof cb === 'function') {
        this.callbacks[eventName].push(cb);
    }
    return this;
};
AddressLookup.prototype.runCBs = function (eventName, args) {
    for (const key in this.callbacks[eventName]) {
        if (this.callbacks[eventName].hasOwnProperty(key)) {
            this.callbacks[eventName][key].call(this, args);
        }
    }
};
AddressLookup.prototype.onComplete = function (cb) {
    return this.on('onComplete', cb);
};
AddressLookup.prototype.onFullAddressFocus = function (cb) {
    return this.on('onFullAddressFocus', cb);
};
AddressLookup.prototype.onFullAddressUpdated = function (cb) {
    return this.on('onFullAddressUpdated', cb);
};
AddressLookup.prototype.onStreetNumberUpdated = function (cb) {
    return this.on('onStreetNumberUpdated', cb);
};
AddressLookup.prototype.onSuggestionClicked = function (cb) {
    return this.on('onSuggestionClicked', cb);
};
AddressLookup.prototype.onSuggestionSelected = function (cb) {
    return this.on('onSuggestionSelected', cb);
};
AddressLookup.prototype.onConfirm = function (cb) {
    return this.on('onConfirm', cb);
};
AddressLookup.prototype.onSuggestionsDisplayed = function (cb) {
    return this.on('onSuggestionsDisplayed', cb);
};
AddressLookup.prototype.onNoSuggestions = function (cb) {
    return this.on('onNoSuggestions', cb);
};
AddressLookup.prototype.onFullAddressMissing = function (cb) {
    return this.on('onFullAddressMissing', cb);
};
AddressLookup.prototype.onRequestStreetNumber = function (cb) {
    return this.on('onRequestStreetNumber', cb);
};
AddressLookup.prototype.onRequestBeMoreSpecific = function (cb) {
    return this.on('onRequestBeMoreSpecific', cb);
};
AddressLookup.prototype.onGeocoderSuggestion = function (cb) {
    return this.on('onGeocoderSuggestion', cb);
};
AddressLookup.prototype.onGeoLocate = function (cb) {
    return this.on('onGeoLocate', cb);
};
AddressLookup.prototype.onGeoLocateError = function (cb) {
    return this.on('onGeoLocateError', cb);
};
AddressLookup.prototype.onGeoLocateSuggestions = function (cb) {
    return this.on('onGeoLocateSuggestions', cb);
};
AddressLookup.prototype.onGeoLocateSelect = function (cb) {
    return this.on('onGeoLocateSelect', cb);
};

AddressLookup.getAddressFromUser = function (selector, cb, options = {}) {
    logger.debug(filename, 'getAddressFromUser');

    return new Promise((resolve) => {
        var addressLookupModal;
        const modalOptions = options.modal || {},
            addressLookupOptions = options.addressLookup || {},
            closeAddressLookup = function (location) {
                if (!addressLookupOptions.leaveModalOpenOnComplete) {
                    addressLookupModal.hide();
                }
                cb({ location, modal: addressLookupModal });
            },
            initialiseAddressLookup = function () {
                addressLookupOptions.context = addressLookupModal.modal.el;
                addressLookupOptions.onComplete = closeAddressLookup;
                if (options.componentRestrictions) {
                    addressLookupOptions.componentRestrictions = options.componentRestrictions;
                }
                AddressLookup.attach('[data-address-lookup-template]', addressLookupOptions).then((al) => {
                    const addressLookup = al;
                    resolve({ addressLookup, addressLookupModal });
                });
            };

        modalOptions.clickOutsideToClose = modalOptions.clickOutsideToClose || 'false';
        modalOptions.className = 'mm-modal-overlay--unFix';
        modalOptions.closeBtn = modalOptions.closeBtn || 'true';
        modalOptions.content = selector;
        modalOptions.modalType = 'fullscreen--touch';
        modalOptions.onShow = initialiseAddressLookup;
        addressLookupModal = new MegaModal(modalOptions);
        addressLookupModal.show();
    });
};

AddressLookup.attach = function (selector, options = {}) {
    logger.debug(filename, 'attach');
    const context = options.context || document;
    const el = context.querySelector(selector);
    return new Promise((resolve) => {
        if (el) {
            const config = el.getAttribute('data-address-lookup');
            let combinedOptions = {};

            if (config) {
                combinedOptions = JSON.parse(config);
            }
            for (const key in options) {
                if (options.hasOwnProperty(key)) {
                    combinedOptions[key] = options[key];
                }
            }
            new GoogleAPILoader(
                () => (window.google && window.google.maps && window.google.maps.places),
                {
                    apiKey: combinedOptions.apiKey,
                    libraries: ['places']
                }
            ).then(() => {
                resolve(new AddressLookup(selector, combinedOptions));
            }).catch(err => {
                logger.error(filename, 'attaching failed', err);
            });
        }
    });
};
